import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import './FactorSensitivityChart.css';

const DEFAULT_MODEL = 'Active';
const DEFAULT_METRIC_KEY = 'factor_sensitivity';
const FALLBACK_UNIVERSES = [
  { key: 'developed', label: 'Developed' },
  { key: 'developed_ex_us', label: 'Developed ex U.S.' },
  { key: 'emerging', label: 'Emerging' },
  { key: 'u.s.', label: 'U.S.' },
  { key: 'north_america', label: 'North America' },
  { key: 'europe', label: 'Europe' },
  { key: 'japan', label: 'Japan' },
  { key: 'asia_pacific_ex_japan', label: 'Asia Pacific ex Japan' },
];

const formatPercent = (value, digits = 2) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return 'N/A';
  }
  return `${(numericValue * 100).toFixed(digits)}%`;
};

const formatNumber = (value, digits = 2) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return 'N/A';
  }
  return numericValue.toFixed(digits);
};

const formatMonthYear = (rawDate) => {
  const parsedDate = new Date(rawDate);
  if (Number.isNaN(parsedDate.getTime())) {
    return 'N/A';
  }
  return parsedDate.toLocaleDateString(undefined, {
    month: 'short',
    year: 'numeric',
  });
};

const buildScatterRows = (payload) => (
  (Array.isArray(payload?.scatter_points) ? payload.scatter_points : [])
    .map((point) => {
      const x = Number(point?.x);
      const y = Number(point?.y);
      if (!Number.isFinite(x) || !Number.isFinite(y)) {
        return null;
      }

      return {
        date: point?.date || '',
        x,
        y,
      };
    })
    .filter(Boolean)
);

const buildRegressionSegment = (payload) => {
  const line = Array.isArray(payload?.regression?.line) ? payload.regression.line : [];
  if (line.length !== 2) {
    return null;
  }

  const [startPoint, endPoint] = line;
  const startX = Number(startPoint?.x);
  const startY = Number(startPoint?.y);
  const endX = Number(endPoint?.x);
  const endY = Number(endPoint?.y);

  if (![startX, startY, endX, endY].every(Number.isFinite)) {
    return null;
  }

  return [
    { x: startX, y: startY },
    { x: endX, y: endY },
  ];
};

const buildCorrelationLabel = (payload) => `Correlation: ${formatNumber(payload?.statistics?.correlation, 2)}`;

const ScatterTooltip = ({ active, payload, chartPayload }) => {
  if (!active || !Array.isArray(payload) || !payload.length) {
    return null;
  }

  const point = payload[0]?.payload;
  if (!point) {
    return null;
  }

  return (
    <div className="factor-sensitivity__tooltip">
      <div className="factor-sensitivity__tooltip-title">{formatMonthYear(point.date)}</div>
      <p className="factor-sensitivity__tooltip-copy">
        {chartPayload?.x_axis_label}: {formatPercent(point.x, 2)}
      </p>
      <p className="factor-sensitivity__tooltip-copy">
        {chartPayload?.y_axis_label}: {formatPercent(point.y, 2)}
      </p>
    </div>
  );
};

const ScatterPanel = ({ payload }) => {
  const scatterRows = useMemo(() => buildScatterRows(payload), [payload]);
  const regressionSegment = useMemo(() => buildRegressionSegment(payload), [payload]);
  const correlationLabel = useMemo(() => buildCorrelationLabel(payload), [payload]);
  const hasData = Number(payload?.aligned_observation_count) > 0 && scatterRows.length > 0;

  return (
    <div className="factor-sensitivity__panel">
      <div className="factor-sensitivity__panel-header">
        <div>
          <Typography variant="h6" className="factor-sensitivity__panel-title">
            {payload?.title || 'Sensitivity'}
          </Typography>
          <Typography variant="body2" className="factor-sensitivity__panel-subtitle">
            {payload?.subtitle || ''}
          </Typography>
        </div>
        <Chip size="small" variant="outlined" label={correlationLabel} />
      </div>

      {hasData ? (
        <div className="factor-sensitivity__chart">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 12, right: 20, left: 12, bottom: 24 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <ReferenceLine x={0} stroke="#475467" strokeOpacity={0.7} />
              <ReferenceLine y={0} stroke="#475467" strokeOpacity={0.7} />
              {regressionSegment && (
                <ReferenceLine
                  segment={regressionSegment}
                  stroke="#0f172a"
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  ifOverflow="extendDomain"
                />
              )}
              <XAxis
                type="number"
                dataKey="x"
                axisLine={false}
                tickLine={false}
                tickFormatter={(value) => formatPercent(value, 0)}
                label={{
                  value: payload?.x_axis_label || '',
                  position: 'insideBottom',
                  offset: -10,
                  style: {
                    fill: '#475467',
                    fontSize: 12,
                  },
                }}
              />
              <YAxis
                type="number"
                dataKey="y"
                width={80}
                axisLine={false}
                tickLine={false}
                tickFormatter={(value) => formatPercent(value, 0)}
                label={{
                  value: payload?.y_axis_label || '',
                  angle: -90,
                  position: 'insideLeft',
                  style: {
                    fill: '#475467',
                    fontSize: 12,
                    textAnchor: 'middle',
                  },
                }}
              />
              <Tooltip
                cursor={{ strokeDasharray: '3 3' }}
                content={<ScatterTooltip chartPayload={payload} />}
              />
              <Scatter
                data={scatterRows}
                fill="#1d4ed8"
                fillOpacity={0.75}
                stroke="#1d4ed8"
                strokeWidth={1}
              />
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <Alert severity="info" sx={{ mt: 2 }}>
          No overlapping monthly observations were available for this scatterplot.
        </Alert>
      )}

    </div>
  );
};

const FactorSensitivityChart = ({
  data,
  submittedData,
  baseUrl,
  metricKey = DEFAULT_METRIC_KEY,
}) => {
  const [selectedType, setSelectedType] = useState(DEFAULT_MODEL);
  const [selectedUniverseKey, setSelectedUniverseKey] = useState('');
  const [customPayload, setCustomPayload] = useState(null);
  const [customizing, setCustomizing] = useState(false);
  const [actionError, setActionError] = useState('');
  const normalizedBaseUrl = (baseUrl || '').replace(/\/$/, '');

  const parseApiJson = async (response, contextLabel) => {
    const rawText = await response.text();
    const trimmedText = rawText.trim();

    try {
      return trimmedText ? JSON.parse(trimmedText) : {};
    } catch {
      const looksLikeHtml =
        trimmedText.toLowerCase().startsWith('<!doctype') ||
        trimmedText.toLowerCase().startsWith('<html');
      if (looksLikeHtml) {
        throw new Error(
          `${contextLabel} returned HTML instead of JSON. Check API base URL/proxy target and backend port.`,
        );
      }
      throw new Error(`${contextLabel} returned invalid JSON.`);
    }
  };

  const defaultAbsolutePayload = data?.Absolute?.[0]?.[metricKey] || null;
  const defaultActivePayload = data?.Active?.[0]?.[metricKey] || null;
  const defaultPayload = data?.[selectedType]?.[0]?.[metricKey] || null;
  const universeSourcePayload = defaultAbsolutePayload || defaultActivePayload || defaultPayload;
  const activePayload = customPayload?.[selectedType] || defaultPayload;
  const availableUniverses = Array.isArray(universeSourcePayload?.available_universes)
    ? universeSourcePayload.available_universes
    : FALLBACK_UNIVERSES;
  const defaultUniverseKey = universeSourcePayload?.default_universe_key || universeSourcePayload?.selected_universe_key || '';

  useEffect(() => {
    if (!selectedUniverseKey && defaultUniverseKey) {
      setSelectedUniverseKey(defaultUniverseKey);
    }
  }, [defaultUniverseKey, selectedUniverseKey]);

  useEffect(() => {
    if (!selectedUniverseKey || !defaultUniverseKey) {
      return undefined;
    }

    if (selectedUniverseKey === defaultUniverseKey) {
      setCustomPayload(null);
      setActionError('');
      setCustomizing(false);
      return undefined;
    }

    if (!submittedData) {
      setCustomPayload(null);
      setActionError('The submitted comparison inputs are unavailable, so universe overrides are disabled.');
      return undefined;
    }

    let cancelled = false;
    const controller = new AbortController();

    const refreshUniverse = async () => {
      setCustomizing(true);
      setActionError('');

      try {
        const response = await fetch(`${normalizedBaseUrl}/api/return-attribution-factor-sensitivity`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            data: submittedData,
            universe_key: selectedUniverseKey,
          }),
          signal: controller.signal,
        });
        const payload = await parseApiJson(response, 'Factor sensitivity API');
        if (!response.ok) {
          throw new Error(payload.error || `Factor sensitivity refresh failed (${response.status})`);
        }

        if (!cancelled) {
          setCustomPayload(payload);
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }
        if (!cancelled) {
          setCustomPayload(null);
          setActionError(error.message || 'Unable to refresh factor sensitivity charts.');
        }
      } finally {
        if (!cancelled) {
          setCustomizing(false);
        }
      }
    };

    refreshUniverse();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [defaultUniverseKey, normalizedBaseUrl, selectedUniverseKey, submittedData]);

  const handleTypeChange = (event, nextType) => {
    void event;
    if (nextType !== null) {
      setSelectedType(nextType);
    }
  };

  if (!activePayload) {
    return (
      <Alert severity="info">
        Benchmark and factor sensitivity results will appear here once this analysis stage completes.
      </Alert>
    );
  }

  const factorScatters = Array.isArray(activePayload?.factor_scatters) ? activePayload.factor_scatters.slice(0, 3) : [];
  const scatterPanels = [activePayload?.benchmark_scatter, ...factorScatters].filter(Boolean);

  return (
    <Box className="factor-sensitivity">
      <div className="factor-sensitivity__controls">
        <div className="factor-sensitivity__controls-left">
          <ToggleButtonGroup
            value={selectedType}
            exclusive
            onChange={handleTypeChange}
            aria-label="Factor sensitivity return type"
            size="small"
          >
            <ToggleButton value="Absolute" aria-label="Absolute">
              Absolute
            </ToggleButton>
            <ToggleButton value="Active" aria-label="Active">
              Active
            </ToggleButton>
          </ToggleButtonGroup>
        </div>

        <div className="factor-sensitivity__controls-right">
          <TextField
            select
            label="Fama/French Universe"
            size="small"
            value={selectedUniverseKey}
            onChange={(event) => setSelectedUniverseKey(event.target.value)}
            className="factor-sensitivity__universe-select"
          >
            {availableUniverses.map((option) => (
              <MenuItem key={option.key} value={option.key}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </div>
      </div>

      {actionError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {actionError}
        </Alert>
      )}

      {customizing && (
        <div className="factor-sensitivity__loading">
          <CircularProgress size={18} />
          <Typography variant="body2">Refreshing scatterplots for the selected universe.</Typography>
        </div>
      )}

        <div className="factor-sensitivity__grid factor-sensitivity__grid--quad">
          {scatterPanels.map((panelPayload, index) => (
            <ScatterPanel
              key={`${panelPayload?.title || 'panel'}-${index}`}
              payload={panelPayload}
            />
          ))}
        </div>
    </Box>
  );
};

export default FactorSensitivityChart;
